import { FC, useCallback, useEffect, useState } from 'react';

import { Box, Header, Icon, ListItemCheckboxGroup } from '@rbilabs/universal-components';

import { IItem, ILegacyCartEntry } from '@rbi-ctg/menu';
import { A11yWrapper } from 'components/a11y-wrapper';
import { FoodwareDebug } from 'components/debug-menu/foodware-debug';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { ItemAvailabilityStatus } from 'enums/menu';
import { useCart } from 'hooks/use-cart';
import useFoodware from 'hooks/use-foodware';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { createCartEntry } from 'utils/cart';

import { Container, DescriptionContainer } from './styled';

/**
 *
 * Foodware
 *
 */
export const Foodware: FC = () => {
  const isDebugMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_DEBUG_TOOL);
  const isUnifiedMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_UNIFIED_MENU);

  const { addItemToCart, cartEntries, removeFromCart } = useCart();
  const { getPricingAndAvailability, getOneMenuItemAvailability } = useMenuContext();
  const { featureFoodware, loading } = useFoodware();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleChange = useCallback(
    (nextValues: any) => {
      // Find foodware items added
      const addItem = nextValues.find((value: string) => !selectedItems.includes(value));
      // Find foodware items removed
      const removeItem = selectedItems.find((value: string) => !nextValues.includes(value));
      if (addItem) {
        // Convert added sanity item to cart item
        const item = featureFoodware?.items?.find(item => item?._id === addItem) as IItem;
        const { price } = getPricingAndAvailability(item._id) || {};
        if (item) {
          const cartEntry: ILegacyCartEntry = {
            ...createCartEntry({
              item,
              price: price?.default || price?.min || 0,
            }),
            isExtra: true,
          };
          // Add item to the cart, we don't need to hydrate here.
          addItemToCart(cartEntry, undefined, true, true);
        }
      }
      if (removeItem) {
        // Find cart entry
        const cartEntry = cartEntries.find((cartEntry: any) => cartEntry._id === removeItem);
        if (cartEntry) {
          // Remove item from the cart
          removeFromCart({ cartId: cartEntry.cartId });
        }
      }
      setSelectedItems(nextValues);
    },
    [
      addItemToCart,
      cartEntries,
      featureFoodware?.items,
      getPricingAndAvailability,
      removeFromCart,
      selectedItems,
    ]
  );

  const listItems: any =
    featureFoodware?.items?.map(item => {
      const isAvailable = Boolean(
        item &&
          (isUnifiedMenuEnabled
            ? getOneMenuItemAvailability(item._id).availabilityStatus ===
              ItemAvailabilityStatus.AVAILABLE
            : getPricingAndAvailability(item._id)?.isAvailable)
      );

      const label = (item?.name?.locale || '') + (isAvailable ? '' : ` (${t('unavailable')})`);

      return {
        value: item?._id || '',
        label,
        accessibilityLabel: label,
        disabled: !isAvailable,
      };
    }) || [];

  // We only want to initialize the foodware container as expanded if utensils are found in the cart during the first render.
  useEffect(() => {
    if (isInitialized && selectedItems.length) {
      return;
    }
    // Check for foodware items in the cart
    const initSelectedItems: string[] = [];
    cartEntries?.forEach(cartEntry => {
      const foodWareItem = featureFoodware?.items?.find(item => item?._id === cartEntry._id);
      if (foodWareItem && !initSelectedItems.includes(foodWareItem._id)) {
        initSelectedItems.push(foodWareItem._id);
      }
    });
    // Select foodware items that are already in the cart
    if (initSelectedItems.length) {
      setSelectedItems(initSelectedItems);
      setIsExpanded(true);
    }
    setIsInitialized(true);
  }, [cartEntries, featureFoodware?.items, isInitialized, selectedItems, selectedItems.length]);

  if (loading) {
    return null;
  }

  return (
    <Container>
      <A11yWrapper>
        <Header variant="headerTwo" focusable>
          {featureFoodware?.title?.locale}
        </Header>
      </A11yWrapper>
      <DescriptionContainer
        onPress={handleToggle}
        accessible
        accessibilityRole="togglebutton"
        accessibilityState={{ expanded: isExpanded }}
        aria-expanded={isExpanded}
      >
        <SanityBlockRenderer maxWidth="90%" content={featureFoodware?.description?.localeRaw} />
        <Box>
          <Icon variant={isExpanded ? 'collapse' : 'select'} alignSelf={'flex-end'} />
        </Box>
      </DescriptionContainer>

      {isExpanded && (
        <>
          {isDebugMenuEnabled && <FoodwareDebug />}
          <ListItemCheckboxGroup
            value={selectedItems}
            onChange={handleChange}
            listItems={listItems}
          />
        </>
      )}
    </Container>
  );
};
